<template>
  <div>
    <Card
      v-if="validActionCodeProvided"
      :title="$t('general-activation.title')"
      v-bind:footerDivider="false"
    >
      <template v-slot:content>
        <div>
          <p>{{ $t('activationStatus.open') }}</p>
          <p
            v-if="showGeneralError"
            class="error"
            v-dompurify-html="$t('general-activation.error')"
          ></p>
          <div id="username">
            <BasicTextfield
              @input="onUsernameInput"
              id="activation.username"
              :label="$t('username-activation.label')"
              :placeholder="$t('username-activation.placeholder')"
              :required="true"
              :status="usernameTextfieldStatus"
              :value="username"
              inputmode="email"
              @error="toggleUsernameError"
              @update:blur="validateUsername"
            >
              <template v-slot:error>{{ usernameErrorMsg }}</template>
              <template v-slot:info>
                <div v-dompurify-html="$t('username-activation.info')"></div>
                <ul class="list-dots">
                  <li>{{ $t('username-activation.validation.uppercase') }}</li>
                  <li>{{ $t('username-activation.validation.lowercase') }}</li>
                  <li>{{ $t('username-activation.validation.digits') }}</li>
                  <li>{{ $t('username-activation.validation.specialCharacters') }}</li>
                  <li>{{ $t('username-activation.validation.length') }}</li>
                </ul>
              </template>
            </BasicTextfield>
          </div>
          <div id="password">
            <BasicTextfield
              @input="onPasswordInput"
              id="activation.password"
              type="password"
              :label="$t('password-activation.label')"
              :passwordVisibilityTrigger="true"
              :required="true"
              :status="passwordTextfieldStatus"
              :value="password"
              @update:blur="validatePassword"
            >
              <template v-slot:error>{{ passwordErrorMsg }}</template>
              <template v-slot:info>
                <div v-dompurify-html="$t('password-activation.info')"></div>

                <ValidationRule :valid="uppercaseValidator()">
                  {{ $t('password-activation.validation.uppercase') }}
                </ValidationRule>

                <ValidationRule :valid="lowercaseValidator()">
                  {{ $t('password-activation.validation.lowercase') }}
                </ValidationRule>

                <ValidationRule :valid="digitsValidator()">
                  {{ $t('password-activation.validation.digits') }}
                </ValidationRule>

                <ValidationRule :valid="specialCharacterValidator()">
                  {{ $t('password-activation.validation.specialCharacters') }}
                </ValidationRule>

                <ValidationRule :valid="lengthValidator()">
                  {{ $t('password-activation.validation.length') }}
                </ValidationRule>
              </template>
            </BasicTextfield>
          </div>
          <div id="passwordConfirmation">
            <BasicTextfield
              @input="onPasswordConfirmationInput"
              id="activation.passwordConfirmation"
              type="password"
              :label="$t('passwordConfirmation-activation.label')"
              :passwordVisibilityTrigger="true"
              :required="true"
              :status="passwordConfirmationTextfieldStatus"
              :value="passwordConfirmation"
              @update:blur="validatePasswordConfirmation"
            >
              <template v-slot:error>
                <div v-dompurify-html="$t('passwordConfirmation-activation.error')"></div>
              </template>
              <template v-slot:info>
                <div v-dompurify-html="$t('passwordConfirmation-activation.info')"></div>
              </template>
            </BasicTextfield>
          </div>
          <div v-if="showNewEmail()" id="email">
            <BasicTextfield
              @input="onNewEmailInput"
              id="activation.email"
              :label="$t('email-activation.label')"
              :required="false"
              :status="newEmailTextfieldStatus"
              :value="newEmail"
              inputmode="email"
              @error="toggleNewEmailError"
              @update:blur="validateEmail"
            >
              <template v-slot:error>{{ newEmailErrorMessage }}</template>
              <template v-slot:info>
                <div v-dompurify-html="$t('email-activation.info')"></div>
              </template>
            </BasicTextfield>
          </div>
          <div v-if="showNewEmail()" id="email-repeat">
            <BasicTextfield
              @input="onEmailRepeatInput"
              id="activation.emailRepeat"
              :label="$t('emailRepeat-activation.label')"
              :required="false"
              :status="emailRepeatTextfieldStatus"
              :value="emailRepeat"
              inputmode="email"
              @error="toggleEmailRepeatError"
              @update:blur="validateEmailRepeat"
            >
              <template v-slot:error>
                <div v-dompurify-html="$t('emailRepeat-activation.error')"></div>
              </template>
            </BasicTextfield>
          </div>
          <div id="birthdate">
            <DateInput
              v-model="birthdate"
              @input="onBirthdateInput"
              id="activation.birthdate"
              :label="$t('birthdate-activation.label')"
              :minValidYear="18"
              :placeholder="$t('birthdate-activation.placeholder')"
              :required="true"
              :status="birthdateTextfieldStatus"
              idProp="activation.birthdate_inner"
              mask="DD.MM.YYYY"
              @blur="validateBirthdate"
              @keyup.enter="onSubmit"
              @update:error="notifyErrorDate"
              @update:invalid-date="notifyErrorDate"
            >
              <template v-slot:error>{{ birthdateErrorMsg }}</template>
              <template v-slot:info>{{ $t('birthdate-activation.info') }}</template>
            </DateInput>
          </div>
          <div v-if="showBestandskundenElements()" id="vsnr">
            <BasicTextfield
              @input="onVsnrInput"
              id="activation.vsnr"
              formnovalidate="true"
              :label="$t('vsnr-activation.label')"
              :required="true"
              :status="vsnrTextfieldStatus"
              :value="vsnr"
              @update:blur="validateVsnr"
              @update:error="toggleVsnrError"
            >
              <template v-slot:error>
                <div v-dompurify-html="vsnrErrorMsg"></div>
              </template>
              <template v-slot:info>
                <div v-dompurify-html="$t('vsnr-activation.info')"></div>
              </template>
            </BasicTextfield>
          </div>
          <Checkbox
            v-if="termsAndConditionsRequired"
            v-model="termsAndConditionsChecked"
            @change="checkboxChanged"
            id="activation.terms-of-condition-checkbox"
            class="mt-32"
            :status="termsAndConditionsStatus"
          >
            <template v-slot:label>
              <span>{{ $t('termsAndConditions.agreeToTermsAndConditionsPrefix') }}</span>
              <a
                id="activation.terms-of-usage-page-link"
                target="_blank"
                rel="noopener noreferrer"
                :href="termsOfUsePage"
              >
                {{ $t('termsAndConditions.agreeToTermsAndConditionsLinkText') }}
              </a>
              <span>{{ $t('termsAndConditions.agreeToTermsAndConditionsSuffix') }}</span>
            </template>
            <template v-slot:error>
              <p class="error-message">{{ $t('termsAndConditions.error') }}</p>
            </template>
          </Checkbox>
          <p
            class="mt-32"
            v-dompurify-html="$t('termsAndConditions.dataPrivacy', { privacyPolicyPage })"
          ></p>
        </div>
      </template>
      <template v-slot:footer>
        <div class="footer-cta footer-cta--left">
          <CustomButton
            @click="onSubmit"
            :buttonColor="ButtonColor.PRIMARY"
            :disabledState="!isFormValid()"
            :pendingState="pending"
            buttonId="activation.aktivieren-button"
            >{{ $t('labels.submitButton') }}
          </CustomButton>
        </div>
      </template>
    </Card>
    <PukConfirmed v-if="activationSucceeded" :username="username"></PukConfirmed>
  </div>
</template>

<script lang="ts" setup>
import dayjs, { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import {
  BasicTextfield,
  ButtonColor,
  Card,
  Checkbox,
  CustomButton,
  DateInput,
  InputStatus,
  ValidationRule
} from 'de-zurich-ui-library'
import { navigate } from '@/utils/NavigationUtils'
import { escapeVSNR, isValidVSNR } from '@/utilsTenant/rules'
import { isAuthenticated } from '@/utils/KeycloakUtils'
import { onBeforeMount, onMounted, ref } from 'vue'
import PukConfirmed from '@/components/account-management/idlevelupgrade/PukConfirmed.vue'
import { useI18n } from 'vue-i18n'
import {
  setInitialCredentials,
  retrievePreparedAccount
} from '@/stores/accountManagementService'
import { ResponseError } from 'account-api'

const { t } = useI18n()

dayjs.extend(customParseFormat)

const isFormValid = (): boolean => {
  return (
    usernameIsValid() &&
    passwordIsValid() &&
    passwordConfirmationIsValid() &&
    termsAndConditionsIsValid() &&
    birthdateIsValid() &&
    vsnrIsValid()
  )
}

const uppercaseValidator = (): boolean => {
  return /[A-Z]/.test(password.value)
}

const lowercaseValidator = (): boolean => {
  return /[a-z]/.test(password.value)
}

const digitsValidator = (): boolean => {
  return /\d/.test(password.value)
}

const specialCharacterValidator = (): boolean => {
  return /[_\-.+@&;!$%]+/.test(password.value)
}

const lengthValidator = (): boolean => {
  return /^.{8,60}$/.test(password.value)
}

const usernameIsValid = (): boolean => {
  return usernameTextfieldStatus.value === InputStatus.VALID
}

const passwordIsValid = (): boolean => {
  return passwordTextfieldStatus.value === InputStatus.VALID
}

const passwordConfirmationIsValid = (): boolean => {
  return passwordConfirmationTextfieldStatus.value === InputStatus.VALID
}

const birthdateIsValid = (): boolean => {
  return birthdateTextfieldStatus.value === InputStatus.VALID
}

const vsnrIsValid = (): boolean => {
  return vsnrTextfieldStatus.value === InputStatus.VALID || !showBestandskundenElements()
}

const termsAndConditionsIsValid = (): boolean => {
  return (
    (termsAndConditionsRequired.value === true &&
      termsAndConditionsChecked.value === true) ||
    termsAndConditionsRequired.value === false
  )
}

const showBestandskundenElements = (): boolean => {
  return policyNumberRequired.value
}

const getDateFormatISO8601 = (): string => {
  formattedBirthdate.value = null // reset
  const momentDate: Dayjs = dayjs(birthdate.value, 'DD.MM.YYYY', true)
  if (momentDate.isValid()) {
    formattedBirthdate.value = momentDate.format('YYYY-MM-DD')
    return (formattedBirthdate.value = momentDate.format('YYYY-MM-DD'))
  }
  return ''
}

const showNewEmail = (): boolean => {
  return showNewEmailValue.value
}

const activationCode = ref('')
const username = ref('')
const usernameTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const usernameErrorMsg = ref('')
const usernameRegex = /^[a-zA-Z0-9_\-.+@&;!$%]{7,60}$/
const newEmail = ref('')
const newEmailTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const newEmailErrorMessage = ref('')
const emailRepeat = ref('')
const emailRepeatTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const password = ref('')
const passwordTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const passwordErrorMsg = ref('')
const passwordRegex = /^[a-zA-Z0-9_\-.+@&;!$%]{8,60}$/
const passwordConfirmation = ref('')
const passwordConfirmationTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const birthdate = ref('')
const formattedBirthdate = ref<string | null>(null)

const birthdateTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const birthdateErrorMsg = ref('')
const vsnr = ref('')
const vsnrTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const vsnrErrorMsg = ref('')
const companyCode = ref('')
const id = ref('')
const policyNumberRequired = ref(false)
const termsOfUsePage: string =
  globalThis.ENV_CONFIG?.REDIRECTS.ACCOUNT_ACTIVATION_TERMS_OF_USE_PAGE
const privacyPolicyPage: string =
  globalThis.ENV_CONFIG?.REDIRECTS.ACCOUNT_ACTIVATION_PRIVACY_POLICY_PAGE
const termsAndConditionsRequired = ref(true)
const termsAndConditionsStatus = ref<InputStatus>(InputStatus.DEFAULT)
const termsAndConditionsChecked = ref(false)
const validActionCodeProvided = ref(false)
const showGeneralError = ref(false)
const pending = ref(false)
const activationSucceeded = ref(false)
const loggedIn = ref(false)
const showNewEmailValue = ref(false)

onMounted(async () => {
  loggedIn.value = isAuthenticated()
})

onBeforeMount(async () => {
  const code: string | null = new URLSearchParams(globalThis.location.search).get('code')
  if (code) {
    activationCode.value = code

    try {
      const res = await retrievePreparedAccount(code)
      showNewEmailValue.value = !res.isMailValid
      id.value = res.id ?? ''
      policyNumberRequired.value = res.policyNumberRequired ?? false
      termsAndConditionsRequired.value = res.termsOfUseAgreementRequired ?? true
      username.value = ''
      companyCode.value = res.companyCode ?? ''
      // Validation of termsAndConditionsRequired because if not required, validation must be triggered manually because can't be triggered by user
      if (!termsAndConditionsRequired.value) validateTermsAndConditions()

      validActionCodeProvided.value = true
    } catch {
      navigate(globalThis.ENV_CONFIG?.REDIRECTS.ACCOUNT_ACTIVATION_INVALID_CODE_PAGE)
    }
  } else {
    // no activation code provided
    navigate(globalThis.ENV_CONFIG?.REDIRECTS.ACCOUNT_ACTIVATION_INVALID_CODE_PAGE)
  }
})
const onBirthdateInput = (): void => {
  validateBirthdate()
}

const onUsernameInput = (val: string): void => {
  username.value = val
  validateUsername()
}

const onEmailRepeatInput = (val: string): void => {
  emailRepeat.value = val
  if (emailRepeatTextfieldStatus.value === InputStatus.ERROR) {
    validateEmailRepeat()
  }
}

const onNewEmailInput = (val: string): void => {
  newEmail.value = val
  if (newEmailTextfieldStatus.value === InputStatus.ERROR) {
    validateEmail()
  }
}

const onPasswordInput = (val: string): void => {
  password.value = val
  validatePassword()
}

const onPasswordConfirmationInput = (val: string): void => {
  passwordConfirmation.value = val
  validatePasswordConfirmation()
}

const onVsnrInput = (val: string): void => {
  vsnr.value = val.trim()
  validateVsnr()
}

const validateAll = (): void => {
  validateUsername()
  validatePassword()
  validatePasswordConfirmation()
  validateBirthdate()
  validateVsnr()
  validateTermsAndConditions()
  validateEmail()
  validateEmailRepeat()
}

const onSubmit = async (): Promise<void> => {
  validateAll()

  pending.value = true
  // This ensures, that we check all validities which can then be reported in the UI
  const {
    passwordValid,
    passwordConfirmationValid,
    usernameValid,
    birthdateValid,
    vsnrValid,
    termsAndConditionsValid,
    newEmailValid,
    emailRepeatValid
  } = {
    passwordValid: passwordIsValid(),
    passwordConfirmationValid: passwordConfirmationIsValid(),
    usernameValid: usernameIsValid(),
    birthdateValid: birthdateIsValid(),
    vsnrValid: vsnrIsValid(),
    termsAndConditionsValid: termsAndConditionsIsValid(),
    newEmailValid: validateEmail(),
    emailRepeatValid: validateEmailRepeat()
  }
  if (
    !passwordValid ||
    !passwordConfirmationValid ||
    !usernameValid ||
    !birthdateValid ||
    !vsnrValid ||
    !termsAndConditionsValid ||
    (!newEmailValid && showNewEmail()) ||
    (!emailRepeatValid && showNewEmail())
  ) {
    pending.value = false
    return
  }

  formattedBirthdate.value = getDateFormatISO8601()

  interface Body {
    username: string
    password: string
    emailCode: string
    birthdate: string
    vsnr?: string

    newEmail?: string
    termsAndConditionsAccepted: boolean
  }

  const body: Body = {
    username: username.value,
    password: password.value,
    emailCode: activationCode.value,
    birthdate: formattedBirthdate.value || birthdate.value,
    termsAndConditionsAccepted: termsAndConditionsChecked.value,
    ...(newEmail.value !== '' && { newEmail: newEmail.value })
  }

  if (showBestandskundenElements()) {
    body.vsnr = escapeVSNR(vsnr.value.trim())
  }

  try {
    await setInitialCredentials(id.value, body)
    pending.value = false
    activationSucceeded.value = true
    validActionCodeProvided.value = false
  } catch (e) {
    pending.value = false
    if (e instanceof ResponseError) {
      let isBusinessError = false
      const error = await e.response.json()
      switch (
        error.code // Match ApiErrorMessageAccountManagement.java error codes
      ) {
        case 'E403-001-AMS': // General "Input does not match expectations"
          isBusinessError = true
          showGeneralError.value = true
          break
        case 'E404-001-AMS': // No account matches the email code
          isBusinessError = true
          showGeneralError.value = true
          break
        case 'E404-002-AMS': // No account matches input
          isBusinessError = true
          showGeneralError.value = true
          break
        case 'E409-001-AMS': // Username already in use
          usernameErrorMsg.value = String(t('username-activation.errors.alreadyInUse'))
          usernameTextfieldStatus.value = InputStatus.ERROR
          isBusinessError = true
          break
        case 'E400-001-CSL-1':
          isBusinessError = true
          showGeneralError.value = true
          break
      }

      if (!isBusinessError) {
        navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
      }
    } else {
      navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
    }
  }
}

const validateUsername = (): void => {
  if (!username.value || username.value === '') {
    usernameTextfieldStatus.value = InputStatus.ERROR
    usernameErrorMsg.value = String(t('username-activation.errors.empty'))
  } else if (username.value.length < 7) {
    usernameErrorMsg.value = String(
      t('username-activation.errors.tooShort', {
        chars: username.value.length
      })
    )
    usernameTextfieldStatus.value = InputStatus.ERROR
  } else if (username.value.length > 60) {
    usernameErrorMsg.value = String(
      t('username-activation.errors.tooLong', {
        chars: username.value.length
      })
    )
    usernameTextfieldStatus.value = InputStatus.ERROR
  } else if (!usernameRegex.test(username.value)) {
    usernameErrorMsg.value = String(t('username-activation.errors.rulesViolated'))
    usernameTextfieldStatus.value = InputStatus.ERROR
  } else {
    usernameTextfieldStatus.value = InputStatus.VALID
  }
}

const validateEmail = (): boolean => {
  if (!newEmail.value || newEmail.value === '') {
    newEmailTextfieldStatus.value = InputStatus.ERROR
    newEmailErrorMessage.value = String(t('email-activation.errors.empty'))
  } else if (!/^\S+@\S+\.\S+$/.test(newEmail.value)) {
    newEmailTextfieldStatus.value = InputStatus.ERROR
    newEmailErrorMessage.value = String(t('email-activation.errors.invalidEmail'))
  } else {
    newEmailTextfieldStatus.value = InputStatus.VALID
  }
  return newEmailTextfieldStatus.value === InputStatus.VALID
}

const validateEmailRepeat = (): boolean => {
  emailRepeatTextfieldStatus.value =
    newEmail.value === emailRepeat.value ? InputStatus.VALID : InputStatus.ERROR
  return emailRepeatTextfieldStatus.value === InputStatus.VALID
}

const toggleNewEmailError = (): void => {
  newEmailTextfieldStatus.value = InputStatus.ERROR
}

const atLeastThreeRulesValidator = (_password: string): boolean => {
  const testResult = {
    lengthSufficient: lengthValidator(),
    uppercasePresent: uppercaseValidator(),
    lowercasePresent: lowercaseValidator(),
    digitsPresent: digitsValidator(),
    specialCharactersPresent: specialCharacterValidator(),
    enoughRulesMet: () => {
      let rulesMet = 0
      rulesMet += testResult.uppercasePresent ? 1 : 0
      rulesMet += testResult.lowercasePresent ? 1 : 0
      rulesMet += testResult.digitsPresent ? 1 : 0
      rulesMet += testResult.specialCharactersPresent ? 1 : 0
      return rulesMet >= 3
    }
  }
  return testResult.lengthSufficient && testResult.enoughRulesMet()
}

const validatePassword = (): void => {
  if (passwordConfirmation.value.length > 0) {
    validatePasswordConfirmation()
  }

  if (!password.value) {
    passwordTextfieldStatus.value = InputStatus.ERROR
    passwordErrorMsg.value = String(t('password-activation.errors.empty'))
  } else if (!lengthValidator) {
    passwordTextfieldStatus.value = InputStatus.ERROR
    passwordErrorMsg.value = String(t('password-activation.errors.lengthViolate'))
  } else if (!atLeastThreeRulesValidator(password.value)) {
    passwordTextfieldStatus.value = InputStatus.ERROR
    passwordErrorMsg.value = String(t('password-activation.errors.rulesViolated'))
  } else if (!passwordRegex.test(password.value)) {
    passwordTextfieldStatus.value = InputStatus.ERROR
    passwordErrorMsg.value = String(t('password-activation.errors.unsupportedCharacters'))
  } else {
    passwordTextfieldStatus.value = InputStatus.VALID
  }
}

const validatePasswordConfirmation = (): void => {
  if (password.value === passwordConfirmation.value) {
    passwordConfirmationTextfieldStatus.value = InputStatus.VALID
  } else {
    passwordConfirmationTextfieldStatus.value = InputStatus.ERROR
  }
}

const validateBirthdate = (): void => {
  if (!birthdate.value || birthdate.value.length < 10) {
    birthdateTextfieldStatus.value = InputStatus.ERROR
    birthdateErrorMsg.value = String(t('birthdate-activation.errors.wrongFormat'))
  } else {
    birthdateTextfieldStatus.value = InputStatus.VALID
  }
}

const validateVsnr = (): void => {
  // If the vsnr is not required, it cannot be invalid
  if (!showBestandskundenElements()) {
    vsnrTextfieldStatus.value = InputStatus.VALID
  } else if (!vsnr.value || vsnr.value === '') {
    vsnrTextfieldStatus.value = InputStatus.ERROR
    vsnrErrorMsg.value = String(t('vsnr-activation.errors.empty'))
  } else if (vsnr.value !== '' && !isValidVSNR(vsnr.value)) {
    vsnrTextfieldStatus.value = InputStatus.ERROR
    vsnrErrorMsg.value = String(t('vsnr-activation.errors.invalidFormat'))
  } else {
    vsnrTextfieldStatus.value = InputStatus.VALID
  }
}

const validateTermsAndConditions = (): void => {
  if (!termsAndConditionsChecked.value && termsAndConditionsRequired.value) {
    termsAndConditionsStatus.value = InputStatus.ERROR
  } else if (termsAndConditionsChecked.value || !termsAndConditionsRequired.value) {
    termsAndConditionsStatus.value = InputStatus.VALID
  }
}

const toggleUsernameError = (): void => {
  validateUsername()
}

const toggleEmailRepeatError = (): void => {
  validateEmailRepeat()
}
const toggleVsnrError = (): void => {
  validateVsnr()
}

const checkboxChanged = (_checked: boolean): void => {
  termsAndConditionsChecked.value = _checked
  validateTermsAndConditions()
}

const notifyErrorDate = (): void => {
  birthdateTextfieldStatus.value = InputStatus.ERROR
  birthdateErrorMsg.value = String(t('birthdate-activation.errors.wrongFormat'))
}
</script>

<style lang="scss" scoped>
.checkbox__error p {
  color: var(--input-color-error) !important;
  font-size: var(--form-error-font-size) !important;
}

b.error-message {
  padding-left: 5px;
}

.card {
  min-height: 250px;
}

ul {
  list-style: unset !important;
  list-style-position: inside !important;
}
</style>
